<script setup>
/**
 * A case's current stage, coloured by its system category.
 *
 * Stages are dual-labelled: the internal `name` the team works with, and the
 * optional `customerLabel` whose presence is what makes the stage
 * customer-visible. Internal is the default everywhere; only the progress feed
 * and the settings preview ask for the customer-facing label, so the two can
 * never be confused by accident.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import {
  stageCategoryMeta,
  stageLabel,
} from 'dashboard/helper/supportCaseMeta';

const props = defineProps({
  stage: {
    type: Object,
    required: true,
  },
  customerFacing: {
    type: Boolean,
    default: false,
  },
  compact: {
    type: Boolean,
    default: true,
  },
});

const { t } = useI18n();

const meta = computed(() => stageCategoryMeta(props.stage?.category));
const title = computed(() => stageLabel(props.stage, props.customerFacing));
// The system category rides along as the hover description, so a workspace's
// own stage naming never hides which category it reports as.
const label = computed(() => ({
  title: title.value,
  description: t(meta.value.labelKey),
}));
</script>

<template>
  <Label
    :label="label"
    :color="meta.color"
    :compact="compact"
    data-testid="stage-pill"
  >
    <template #icon>
      <Icon
        :icon="meta.icon"
        class="flex-shrink-0 size-3"
        :class="meta.iconClass"
      />
    </template>
  </Label>
</template>
